// TRIAGE-01 (audit decision 3.2 / IMP-01) — per-user triage preferences:
// the configurable keymap + the auto-advance toggle.
//
// ── Persistence seam ────────────────────────────────────────────────────
// The design ("LysePreferences additive `triage` block") wants these in
// the server-side per-user prefs blob (`/api/auth/v1/me/preferences/lyse`).
// That is a CONTRACT CHANGE: the auth/v1 `LysePreferences` schema is
// `additionalProperties: false` and the gateway DTO runs
// `forbidNonWhitelisted` (services/api-gateway/.../lyse-preferences.dto.ts),
// so a `triage` key would 400 today. Until auth/v1 gains the additive
// block, persistence is a per-user localStorage store behind the
// `TriagePreferencesPersistence` interface below — swapping in a
// server-backed implementation later only touches this file.
//
// Needed contract addition (BE-first, additive auth/v1 minor bump):
//   LysePreferences.triage?: {
//     autoAdvance?: boolean;            // default true
//     keymap?: Record<string, string[]> // action id → chords
//   }
// plus the matching gateway DTO class + `LYSE_PREFERENCE_TOP_LEVEL_KEYS`
// entry. The FE wire-up then mirrors `LysePreferences.tiles`
// (ANA-R3-TILE-03) exactly.
import { create } from "zustand";
import {
  DEFAULT_TRIAGE_KEYMAP,
  parseTriageKeymap,
  type TriageKeymap,
} from "./keymap.js";

export interface TriagePreferences {
  keymap: TriageKeymap;
  /** Q2 (decided 2026-06-11): auto-advance after a decision — ON by default. */
  autoAdvance: boolean;
}

export function triagePreferencesDefaults(): TriagePreferences {
  return {
    keymap: { ...DEFAULT_TRIAGE_KEYMAP },
    autoAdvance: true,
  };
}

/** Lenient parse — garbage in, defaults out; partials fill with defaults. */
export function parseTriagePreferences(raw: unknown): TriagePreferences {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return triagePreferencesDefaults();
  }
  const rec = raw as Record<string, unknown>;
  return {
    keymap: parseTriageKeymap(rec.keymap),
    autoAdvance: typeof rec.autoAdvance === "boolean" ? rec.autoAdvance : true,
  };
}

export interface TriagePreferencesPersistence {
  load: (userId: string) => TriagePreferences;
  save: (userId: string, prefs: TriagePreferences) => void;
  remove: (userId: string) => void;
}

const STORAGE_PREFIX = "wp.lyse.triagePrefs.";

export const localStorageTriagePersistence: TriagePreferencesPersistence = {
  load: (userId) => {
    try {
      const raw = window.localStorage.getItem(STORAGE_PREFIX + userId);
      if (raw === null) return triagePreferencesDefaults();
      return parseTriagePreferences(JSON.parse(raw) as unknown);
    } catch {
      // Corrupt JSON / storage denied — defaults, never a crash.
      return triagePreferencesDefaults();
    }
  },
  save: (userId, prefs) => {
    try {
      window.localStorage.setItem(
        STORAGE_PREFIX + userId,
        JSON.stringify(prefs),
      );
    } catch {
      // Quota / privacy mode — the in-memory store still works for the
      // session; persistence silently degrades.
    }
  },
  remove: (userId) => {
    try {
      window.localStorage.removeItem(STORAGE_PREFIX + userId);
    } catch {
      // ignore
    }
  },
};

interface TriagePreferencesState extends TriagePreferences {
  /** The user the current snapshot was hydrated for (`null` = defaults). */
  hydratedForUserId: string | null;
  /** Swappable persistence backend (tests inject a fake). */
  persistence: TriagePreferencesPersistence;
  hydrate: (userId: string) => void;
  setKeymap: (keymap: TriageKeymap) => void;
  setAutoAdvance: (autoAdvance: boolean) => void;
  /** Reset to factory defaults (Settings "Reset this tab"). */
  resetToDefaults: () => void;
  /** Test seam. */
  __setPersistence: (p: TriagePreferencesPersistence) => void;
}

export const useTriagePreferencesStore = create<TriagePreferencesState>(
  (set, get) => ({
    ...triagePreferencesDefaults(),
    hydratedForUserId: null,
    persistence: localStorageTriagePersistence,

    hydrate: (userId) => {
      if (get().hydratedForUserId === userId) return;
      const prefs = get().persistence.load(userId);
      set({ ...prefs, hydratedForUserId: userId });
    },

    setKeymap: (keymap) => {
      set({ keymap });
      const { hydratedForUserId, persistence, autoAdvance } = get();
      if (hydratedForUserId !== null) {
        persistence.save(hydratedForUserId, { keymap, autoAdvance });
      }
    },

    setAutoAdvance: (autoAdvance) => {
      set({ autoAdvance });
      const { hydratedForUserId, persistence, keymap } = get();
      if (hydratedForUserId !== null) {
        persistence.save(hydratedForUserId, { keymap, autoAdvance });
      }
    },

    resetToDefaults: () => {
      const defaults = triagePreferencesDefaults();
      set({ ...defaults });
      const { hydratedForUserId, persistence } = get();
      if (hydratedForUserId !== null) {
        persistence.remove(hydratedForUserId);
      }
    },

    __setPersistence: (p) => {
      set({ persistence: p, hydratedForUserId: null });
    },
  }),
);
